import { useMemo, useState } from "react";
import {
  Button,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import { useBooking } from "../../context/BookingContext";
import StatusBadge from "./StatusBadge";
import RoomTypeIcon from "./RoomTypeIcon";
import {
  fmtDate,
  fmtDateTime,
  fmtTime,
  HOLDING_STATUSES,
  minutesLeft,
  roomLabel,
  STATUS_COLOR,
  todayStr,
} from "./adminHelpers";
import { assignmentStayWindow } from "../../utils/stayWindow";

/* A booking's roomAssignments[] entry only exists once a room has actually
   been assigned (confirm/partial-confirm), so no separate status allowlist
   is needed for that — but a transfer() resets the booking to PENDING
   without cleaning up its old assignment rows, so PENDING (plus the terminal
   Rejected/Cancelled/Transferred statuses) must still be excluded to avoid
   showing a stale reservation for a room the booking no longer holds. */
const EXCLUDED_API_STATUSES = ["REJECTED", "CANCELLED", "TRANSFERRED", "PENDING"];

/* This room's own stay on a booking. An Extend or a Change Stay Window
   writes the room's expected window (effectiveArrival/effectiveDeparture),
   never the booking's dates, so reading the booking here showed an extended
   or moved stay with its original dates. `tag` says why the two differ. */
export function roomStay(booking, assignment) {
  const arrivalDate = assignment.effectiveArrival || booking.checkIn;
  const arrivalTime = assignment.effectiveArrivalTime || booking.arrivalTime;
  const departureDate = assignment.effectiveDeparture || booking.checkOut;
  const departureTime = assignment.effectiveDepartureTime || booking.departureTime;
  const { start, end } = assignmentStayWindow(booking, assignment);
  let tag = "";
  if (assignment.hasCustomWindow) {
    /* Extended: same arrival, later departure (what Extend does). Anything
       else — a moved arrival, an earlier departure — is Modified. */
    const booked = assignmentStayWindow(booking, null);
    tag = start === booked.start && end > booked.end ? "Extended" : "Modified";
  }
  return { arrivalDate, arrivalTime, departureDate, departureTime, start, end, tag };
}

export function getRoomReservationInfo(room, bookings) {
  const today = todayStr();

  /* Every booking still holding this room, whether it has arrived or not. */
  /* filter, not find: one booking can hold the same room twice — a guest leaves
     early and the room is re-seated to the next arrival on the same indent — and
     `find` dropped the second stay out of the popup entirely. */
  const held = bookings
    .flatMap((booking) =>
      (booking.roomAssignments || [])
        .filter((a) => a.roomId === room.id)
        .map((assignment) => ({ booking, assignment, stay: roomStay(booking, assignment) })),
    )
    .filter(({ booking, assignment }) => {
      if (assignment.isNoShow || assignment.actualCheckout) return false;
      return !EXCLUDED_API_STATUSES.includes(booking._apiStatus);
    });

  /* The guest physically in the room: checked into *this* room and not yet
     checked out of it. Deliberately not read off room.holder — the map keeps
     one holder per room (see RoomMapTab's holdersByRoom), so on a same-day
     turnover that can be the evening's arrival rather than the guest still in
     the bed, which left the popup with no Current Stay at all and the
     occupant demoted into the list below. Restricted to the statuses that
     hold a room so this can't name a stay the map itself calls Vacant, and
     not filtered by date: a guest past their expected checkout is still in
     the room and still belongs here. */
  const current =
    held
      .filter(
        ({ booking, assignment }) =>
          assignment.actualCheckin && HOLDING_STATUSES.includes(booking._apiStatus),
      )
      /* Newest check-in first, so a stale assignment left behind by a bad
         checkout can't outrank the guest most recently put in the room. */
      .sort((x, y) =>
        String(y.assignment.actualCheckin).localeCompare(String(x.assignment.actualCheckin)),
      )[0] || null;

  /* By assignment, not booking: the same booking can hold this room again
     later (a re-seat or a second slice), and that stay belongs in the list
     even while the first one is the Current Stay. */
  const rows = held
    .filter(({ assignment, stay }) => {
      if (assignment.id === current?.assignment.id) return false;
      return stay.departureDate >= today;
    })
    .sort((x, y) => String(x.stay.start).localeCompare(String(y.stay.start)));

  return { current, rows };
}

function StayDate({ date, time }) {
  return (
    <>
      {fmtDate(date)}
      {time && (
        <div style={{ fontSize: "0.85em", color: "rgba(0,0,0,.6)" }}>{fmtTime(time)}</div>
      )}
    </>
  );
}

/* Marks a room whose own dates differ from the booking's, with the booked
   dates on hover, so the admin isn't left wondering why they disagree. */
function StayTag({ stay, booking }) {
  if (!stay.tag) return null;
  return (
    <Label
      size="mini"
      color={stay.tag === "Extended" ? "teal" : "grey"}
      style={{ marginTop: 4 }}
      title={`Booked ${fmtDate(booking.checkIn)} to ${fmtDate(booking.checkOut)}`}
    >
      {stay.tag}
    </Label>
  );
}

/* Front desk for one specific booking. The click is stopped at the cell so it
   doesn't also trigger the row, which navigates away to the booking's page. */
function FrontDeskCell({ bookingId, onOpenFrontDesk }) {
  return (
    <Table.Cell collapsing onClick={(e) => e.stopPropagation()}>
      <Button basic color="blue" size="mini" onClick={() => onOpenFrontDesk(bookingId)}>
        <Icon name="concierge bell" /> Front Desk
      </Button>
    </Table.Cell>
  );
}

export default function RoomReservationsModal({ room, bookings, onClose, onOpenFrontDesk, onViewBooking }) {
  const { setRoomStatus } = useBooking();
  const { current, rows } = useMemo(
    () => getRoomReservationInfo(room, bookings),
    [room, bookings],
  );
  const currentBooking = current?.booking || null;
  const [remark, setRemark] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  /* The room's own condition. roomStatus now only hides it behind Occupied —
     a dirty room merely reserved for later reads Maintenance there too, so the
     chip above and this banner agree. */
  const inMaintenance = room.status === "maintenance";
  const affected = [...(current ? [current] : []), ...rows];
  const hasReservations = affected.length > 0;

  const changeStatus = async (statusValue) => {
    setSubmitting(true);
    setError("");
    try {
      await setRoomStatus(room.id, statusValue, remark);
      setConfirming(false);
      setRemark("");
    } catch (e) {
      setError(e.message || "Could not update the room status.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestMaintenance = () => {
    if (hasReservations && !confirming) setConfirming(true);
    else changeStatus("maintenance");
  };

  return (
    <Modal open size={hasReservations ? "large" : "small"} onClose={onClose} closeIcon>
      <Modal.Header style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
        {/* CombinedIcon centers itself with margin:0 auto (for the room-map
           tile it's normally alone in) — wrapping it as its own flex item
           keeps that auto-margin from soaking up space in this row and
           shoving the room number off to the right. Extra paddingRight
           makes room for its badges, which intentionally overflow the
           icon's own box to the right, so they don't collide with the
           room number text sitting right next to it. */}
        <div style={{ flexShrink: 0, paddingRight: 12 }}>
          <RoomTypeIcon capacity={room.capacity} attrs={room.iconAttrs} color={STATUS_COLOR[room.roomStatus]} />
        </div>
        <span>Room {roomLabel(room.roomNumber, room.guestHouseShortName)}</span>
        {/* Front Desk sits on each booking row below rather than up here: one
            header button has to pick a single booking, and on a turnover day
            the room has two — the guest leaving and the one arriving. */}
        <div style={{ position: "absolute", top: 12, right: 40 }}>
          <Label color={STATUS_COLOR[room.roomStatus]}>{room.roomStatus}</Label>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        {inMaintenance && (
          <Message warning>
            <Icon name="wrench" />
            Under maintenance: {room.maintenanceRemark || "No remark"}
            {minutesLeft(room.maintenanceUntil) && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                ~{minutesLeft(room.maintenanceUntil)} min left
              </div>
            )}
          </Message>
        )}
        {!hasReservations && <p>No current or upcoming reservations for this room.</p>}
        {currentBooking && (
          <Segment>
            <Header as="h5" icon="concierge bell" content="Current Stay" />
            <Table celled compact size="small" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Booking</Table.HeaderCell>
                  <Table.HeaderCell>Guest</Table.HeaderCell>
                  <Table.HeaderCell>Phone</Table.HeaderCell>
                  <Table.HeaderCell>Check-In (Actual)</Table.HeaderCell>
                  <Table.HeaderCell>Expected Check-Out</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row style={{ cursor: "pointer" }} onClick={() => onViewBooking(currentBooking.id)}>
                  <Table.Cell>{currentBooking.bookingCode}</Table.Cell>
                  <Table.Cell>{currentBooking.guestName || currentBooking.applicantName || "—"}</Table.Cell>
                  <Table.Cell>{currentBooking.guestMobileNumber || currentBooking.applicantMobileNumber || "—"}</Table.Cell>
                  <Table.Cell>{fmtDateTime(current.assignment.actualCheckin)}</Table.Cell>
                  {/* This room's own departure, not the booking's: a multi-room
                      booking can have one room extended past the others. */}
                  <Table.Cell>
                    <StayDate date={current.stay.departureDate} time={current.stay.departureTime} />
                    <StayTag stay={current.stay} booking={currentBooking} />
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={currentBooking.status} />
                  </Table.Cell>
                  <FrontDeskCell bookingId={currentBooking.id} onOpenFrontDesk={onOpenFrontDesk} />
                </Table.Row>
              </Table.Body>
            </Table>
          </Segment>
        )}

        {rows.length > 0 && (
          <Segment>
            <Header as="h5" icon="calendar alternate outline" content="Upcoming Reservations" />
            <Table celled compact size="small" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Booking</Table.HeaderCell>
                  <Table.HeaderCell>Guest</Table.HeaderCell>
                  <Table.HeaderCell>Expected Check-In</Table.HeaderCell>
                  <Table.HeaderCell>Expected Check-Out</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map(({ booking, assignment, stay }) => (
                  <Table.Row
                    key={assignment.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => onViewBooking(booking.id)}
                  >
                    <Table.Cell>{booking.bookingCode}</Table.Cell>
                    <Table.Cell>{booking.guestName || booking.applicantName || "—"}</Table.Cell>
                    <Table.Cell>
                      <StayDate date={stay.arrivalDate} time={stay.arrivalTime} />
                    </Table.Cell>
                    <Table.Cell>
                      <StayDate date={stay.departureDate} time={stay.departureTime} />
                      <StayTag stay={stay} booking={booking} />
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={booking.status} />
                    </Table.Cell>
                    <FrontDeskCell bookingId={booking.id} onOpenFrontDesk={onOpenFrontDesk} />
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        )}

        {!inMaintenance && (
          <Form>
            <Form.TextArea
              label="Maintenance Remark (optional)"
              placeholder="e.g. AC repair in progress"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Form>
        )}
        {confirming && (
          <Message warning>
            <Message.Header>This room is held by {affected.length} booking(s)</Message.Header>
            <Message.List>
              {affected.map(({ booking: b, assignment, stay }) => (
                <Message.Item key={assignment.id}>
                  {b.bookingCode} — {b.guestName || b.applicantName || "—"}, {fmtDate(stay.arrivalDate)} to{" "}
                  {fmtDate(stay.departureDate)}
                </Message.Item>
              ))}
            </Message.List>
            <p style={{ marginTop: 8 }}>
              Check-in into this room will be refused until it is marked available again.
            </p>
          </Message>
        )}
        {error && <Message negative content={error} />}
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={confirming ? () => setConfirming(false) : onClose} disabled={submitting}>
          {confirming ? "Back" : "Close"}
        </Button>
        {inMaintenance ? (
          <Button
            positive
            loading={submitting}
            disabled={submitting}
            onClick={() => changeStatus("available")}
          >
            Mark Available
          </Button>
        ) : (
          <Button negative loading={submitting} disabled={submitting} onClick={requestMaintenance}>
            {confirming ? "Yes, Mark Under Maintenance" : "Mark Under Maintenance"}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
